import { Model, type ModelProps } from '@/infra/model'
import { computed, observable } from 'mobx'
import { Id } from '@/infra/id'

export type BookkeepingPlatformConnection =
  QuickBooksAccount | XeroAccount | OtherAccount

export type BookkeepingPlatformConnectionProps =
  QuickBooksAccountTypeProps | XeroAccountTypeProps | OtherAccountTypeProps

/**
 * Props for {@link QuickBooksAccount}.
 */
export interface QuickBooksAccountTypeProps {
  type: 'QuickBooks'
  quickBooks: ModelProps<QuickBooksAccount>
}

export class QuickBooksAccount extends Model<QuickBooksAccount> {
  readonly type = 'QuickBooks'

  @observable
  accessor connectionId = Id.none

  @observable
  accessor realmId = Id.none

  @observable
  accessor companyName = ''

  /**
   * The display ID.
   */
  @computed
  get displayId() {
    return this.realmId
  }

  /**
   * The display name.
   */
  @computed
  get displayName() {
    return this.companyName
  }
}

/**
 * Props for {@link XeroAccount}.
 */
export interface XeroAccountTypeProps {
  type: 'Xero'
  xero: ModelProps<XeroAccount>
}

export class XeroAccount extends Model<XeroAccount> {
  readonly type = 'Xero'

  @observable
  accessor connectionId = Id.none

  @observable
  accessor tenantId = Id.none

  @observable
  accessor tenantName = ''

  /**
   * The display ID.
   */
  @computed
  get displayId() {
    return this.tenantId
  }

  /**
   * The display name.
   */
  @computed
  get displayName() {
    return this.tenantName
  }
}

/**
 * Props for {@link OtherAccount}.
 */
export interface OtherAccountTypeProps {
  type: 'Other'
  other: ModelProps<OtherAccount>
}

export class OtherAccount extends Model<OtherAccount> {
  readonly type = 'Other'

  readonly displayId = null

  readonly displayName = null

  @observable
  accessor info = ''
}

/**
 * Creates the platform connection model for the props.
 */
export function createBookkeepingPlatformConnection(
  props: BookkeepingPlatformConnectionProps,
): BookkeepingPlatformConnection {
  switch (props.type) {
    case 'QuickBooks':
      return new QuickBooksAccount().set(props.quickBooks)
    case 'Xero':
      return new XeroAccount().set(props.xero)
    case 'Other':
      return new OtherAccount().set(props.other)
  }
}
